import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

dayjs.locale("pt-br");

const INTENSITY_CLASSES = [
    "bg-teal-50 text-teal-800 hover:bg-teal-100",
    "bg-teal-200 text-teal-900 hover:bg-teal-300",
    "bg-teal-400 text-white hover:bg-teal-500",
    "bg-teal-600 text-white hover:bg-teal-700",
];

function formatarValorCompacto(valor) {
    if (valor == null) return "";
    const abs = Math.abs(valor);

    if (abs >= 1_000_000) return `R$ ${(valor / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `R$ ${(valor / 1_000).toFixed(1)}mil`;
    return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

function CompensadasCalendar({ service, selectedDate, onSelectDay, refreshKey }) {
    const [currentMonth, setCurrentMonth] = useState(dayjs());
    const [resumo, setResumo] = useState({});
    const [loading, setLoading] = useState(false);

    const ano = currentMonth.year();
    const mes = currentMonth.month() + 1;

    const buscarResumo = useCallback(async () => {
        setLoading(true);
        try {
            const res = await service.resumoCalendario(ano, mes);
            const mapa = {};
            (res.data || []).forEach(dia => {
                mapa[dia.data] = dia;
            });
            setResumo(mapa);
        } finally {
            setLoading(false);
        }
    }, [service, ano, mes]);

    useEffect(() => {
        buscarResumo();
    }, [buscarResumo, refreshKey]);

    const valores = Object.values(resumo).map(d => d.valorTotal || 0).filter(v => v > 0).sort((a, b) => a - b);

    const bucketDoValor = (valor) => {
        if (!valor || valores.length === 0) return -1;
        const posicao = valores.filter(v => v <= valor).length;
        const percentil = posicao / valores.length;
        if (percentil <= 0.25) return 0;
        if (percentil <= 0.5) return 1;
        if (percentil <= 0.75) return 2;
        return 3;
    };

    const startOfMonth = currentMonth.startOf("month");
    const startDay = startOfMonth.day();
    const daysInMonth = currentMonth.daysInMonth();

    const dias = [
        ...Array(startDay).fill(null),
        ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];

    const getDateStr = (day) => currentMonth.date(day).format("YYYY-MM-DD");

    return (
        <div className="rounded-xl border border-border bg-card p-5 shadow-card">
            <div className="flex justify-between items-center mb-4">
                <button
                    onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors"
                    aria-label="Mês anterior"
                >
                    ‹
                </button>

                <span className="font-display font-semibold text-base capitalize select-none text-foreground">
                    {currentMonth.format("MMMM YYYY")}
                </span>

                <button
                    onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors"
                    aria-label="Próximo mês"
                >
                    ›
                </button>
            </div>

            <div className="grid grid-cols-7 text-center text-xs text-muted-foreground mb-1 select-none">
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
                    <div key={d}>{d}</div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
                {dias.map((day, index) => {
                    if (!day) return <div key={index} className="h-16" />;

                    const dateStr = getDateStr(day);
                    const dado = resumo[dateStr];
                    const bucket = dado ? bucketDoValor(dado.valorTotal) : -1;
                    const isSelected = selectedDate === dateStr;
                    const isToday = dayjs().format("YYYY-MM-DD") === dateStr;

                    const corClasse = bucket >= 0 ? INTENSITY_CLASSES[bucket] : "hover:bg-muted text-foreground";

                    return (
                        <button
                            key={index}
                            onClick={() => onSelectDay(dateStr)}
                            className={`h-16 rounded-lg flex flex-col items-center justify-center gap-0.5 text-xs transition-colors cursor-pointer
                                ${corClasse}
                                ${isSelected ? "ring-2 ring-primary" : ""}
                                ${isToday && !isSelected ? "ring-1 ring-current" : ""}`}
                        >
                            <span className="font-medium text-sm">{day}</span>
                            {dado && <span className="text-[10px] leading-tight">{formatarValorCompacto(dado.valorTotal)}</span>}
                        </button>
                    );
                })}
            </div>

            {loading && <div className="text-xs text-muted-foreground mt-2">Carregando...</div>}

            <div className="flex items-center gap-2 mt-4 text-[11px] text-muted-foreground">
                <span>Menor volume</span>
                {INTENSITY_CLASSES.map((c, i) => (
                    <span key={i} className={`w-4 h-4 rounded ${c.split(" ")[0]}`} />
                ))}
                <span>Maior volume</span>
            </div>
        </div>
    );
}

export default CompensadasCalendar;
